/**
 * AttachmentList — lista compacta de archivos adjuntos con descarga + borrado.
 *
 * Props:
 *   attachments: [{file_id, filename, mime, size_bytes, created_at, ...}]
 *   onDelete?: (file_id) => Promise   // si se omite, no se muestra el icono borrar
 *   testId?: string
 *   emptyText?: string                // default: "Sin archivos adjuntos"
 */
import React from "react";
import { FileText, Image as ImageIcon, X, ArrowSquareOut } from "@phosphor-icons/react";

const API_BASE = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

function humanSize(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
}

export default function AttachmentList({ attachments, onDelete, testId = "attachments", emptyText = "Sin archivos adjuntos" }) {
  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-xs text-stone-400 italic" data-testid={`${testId}-empty`}>{emptyText}</div>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {attachments.map((a) => {
        const isImg = (a.mime || "").startsWith("image/");
        const Icon = isImg ? ImageIcon : FileText;
        return (
          <li
            key={a.file_id}
            className="flex items-center gap-2 text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1.5"
            data-testid={`${testId}-item-${a.file_id}`}
          >
            <Icon className="w-3.5 h-3.5 text-stone-500 shrink-0" />
            <a
              href={`${API_BASE}/api/files/${a.file_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-0 truncate hover:underline text-stone-800"
              data-testid={`${testId}-link-${a.file_id}`}
            >
              {a.filename}
            </a>
            <span className="text-stone-400 whitespace-nowrap">{humanSize(a.size_bytes)}</span>
            {a.created_at && (
              <span className="text-stone-400 whitespace-nowrap hidden sm:inline">· {fmtDate(a.created_at)}</span>
            )}
            <ArrowSquareOut className="w-3 h-3 text-stone-400 shrink-0" />
            {onDelete && (
              <button
                onClick={() => onDelete(a.file_id)}
                className="text-stone-400 hover:text-red-700 ml-1"
                aria-label="Eliminar adjunto"
                data-testid={`${testId}-delete-${a.file_id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
